import React, { useState, useEffect } from 'react';
import { useWeddingContext } from '../context/WeddingContext';
import { categories } from '../constants/categories';
import { FilterSettings } from '../types';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({ isOpen, onClose }) => {
  const { state, updateFilterSettings, resetFilters } = useWeddingContext();
  const { filterSettings } = state;

  const [localFilters, setLocalFilters] = useState<FilterSettings>({ ...filterSettings });

  useEffect(() => {
    setLocalFilters({ ...filterSettings });
  }, [filterSettings, isOpen]);

  const handleFilterChange = (field: keyof FilterSettings, value: any) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    updateFilterSettings(localFilters);
    onClose();
  };

  const handleResetFilters = () => {
    resetFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-96 overflow-y-auto">
        {/* ヘッダー */}
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">フィルター</h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="p-4 space-y-4">
          {/* カテゴリフィルター */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">カテゴリ</label>
            <select
              value={localFilters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="all">全てのカテゴリ</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* ステータスフィルター */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ステータス</label>
            <select
              value={localFilters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="all">全てのステータス</option>
              <option value="未着手">未着手</option>
              <option value="進行中">進行中</option>
              <option value="完了">完了</option>
            </select>
          </div>

          {/* 担当者フィルター */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">担当者</label>
            <select
              value={localFilters.assignee}
              onChange={(e) => handleFilterChange('assignee', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="all">全ての担当者</option>
              <option value="新郎">新郎</option>
              <option value="新婦">新婦</option>
              <option value="二人">二人</option>
              <option value="未設定">未設定</option>
            </select>
          </div>

          {/* 期間フィルター */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">期間</label>
            <select
              value={localFilters.period}
              onChange={(e) => handleFilterChange('period', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="all">全期間</option>
              <option value="overdue">期限切れ</option>
              <option value="today">今日期限</option>
              <option value="this_week">今週期限</option>
              <option value="this_month">今月期限</option>
              <option value="next_month">来月期限</option>
              <option value="no_due_date">期限未設定</option>
              <option value="custom_range">日付範囲指定</option>
            </select>
          </div>

          {/* 日付範囲指定（期間フィルターでcustom_rangeが選択された場合に表示） */}
          {localFilters.period === 'custom_range' && (
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
                <input
                  type="date"
                  value={localFilters.dateRangeStart}
                  onChange={(e) => handleFilterChange('dateRangeStart', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
                <input
                  type="date"
                  value={localFilters.dateRangeEnd}
                  onChange={(e) => handleFilterChange('dateRangeEnd', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
                />
              </div>
            </div>
          )}

          {/* 完了タスクを隠すトグル */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="hideCompleted"
              checked={localFilters.hideCompleted}
              onChange={(e) => handleFilterChange('hideCompleted', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="hideCompleted" className="text-sm text-gray-700">
              完了したタスクを隠す
            </label>
          </div>

          {/* グループ化 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">グループ化</label>
            <select
              value={localFilters.grouping}
              onChange={(e) => handleFilterChange('grouping', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="none">グループ化なし</option>
              <option value="period">期間別</option>
              <option value="category">カテゴリ別</option>
              <option value="assignee">担当者別</option>
            </select>
          </div>

          {/* ソート */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">並び順</label>
            <select
              value={localFilters.sort}
              onChange={(e) => handleFilterChange('sort', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-400 outline-none"
            >
              <option value="dueDateAsc">期限日（昇順）</option>
              <option value="dueDateDesc">期限日（降順）</option>
              <option value="createdAtAsc">作成日（昇順）</option>
              <option value="createdAtDesc">作成日（降順）</option>
              <option value="nameAsc">名前（昇順）</option>
              <option value="nameDesc">名前（降順）</option>
              <option value="status">ステータス順</option>
              <option value="category">カテゴリ順</option>
              <option value="assignee">担当者順</option>
            </select>
          </div>
        </div>

        {/* フッター */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200">
          <div className="flex gap-2">
            <button
              onClick={handleResetFilters}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-md transition duration-150"
            >
              リセット
            </button>
            <button
              onClick={handleApplyFilters}
              className="flex-1 bg-pink-500 hover:bg-pink-600 text-white font-medium py-2 px-4 rounded-md transition duration-150"
            >
              適用
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal; 